import { categories, feedItems, type FeedItem } from "@/mocks/feed";
import SectionHeading from "./SectionHeading";

interface EventFeedProps {
  active: string;
  query: string;
  onCategory: (id: string) => void;
  onClearQuery: () => void;
}

export default function EventFeed({ active, query, onCategory, onClearQuery }: EventFeedProps) {
  const q = query.trim().toLowerCase();
  const filtered = feedItems.filter((f) => {
    const inCat = active === "all" || f.category === active;
    const inQ = !q || f.title.toLowerCase().includes(q) || f.snippet.toLowerCase().includes(q) || f.tags.join("").toLowerCase().includes(q);
    return inCat && inQ;
  });

  const featured = filtered.find((f) => f.featured);
  const rest = filtered.filter((f) => f.id !== featured?.id);

  return (
    <section id="feed" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="一手爆料 · 前线内容流"
          title="前线爆料，更新不停"
          desc="现场直击、目击细节与原发经过都会第一时间归档到这里。跟着栏目切，或直接搜关键词，随时回刷都有新爆点。"
        />

        {/* category tabs */}
        <div className="scrollbar-none mt-7 flex items-center gap-2 overflow-x-auto pb-1">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onCategory(c.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                active === c.id ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-foreground-950"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {q ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-search-line text-base text-primary-600" aria-hidden="true"></i>
            正在搜索「<span className="font-semibold text-foreground-950">{query}</span>」，共 {filtered.length} 条结果
            <button
              type="button"
              onClick={onClearQuery}
              className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800 hover:bg-secondary-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line" aria-hidden="true"></i> 清除
            </button>
          </div>
        ) : null}

        {filtered.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-3 text-base font-semibold text-foreground-950">这一栏暂时没有新瓜</h3>
            <p className="mt-1 text-sm text-foreground-600">换一个栏目或清空关键词，前线随时回刷都有新爆点。</p>
            <button
              type="button"
              onClick={() => {
                onCategory("all");
                onClearQuery();
              }}
              className="mt-4 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              查看全部前线
            </button>
          </div>
        ) : (
          <>
            {featured ? (
              <FeaturedCard item={featured} />
            ) : null}
            {rest.length ? (
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((f) => (
                  <Card key={f.id} item={f} />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function Card({ item }: { item: FeedItem }) {
  const catName = categories.find((c) => c.id === item.category)?.name || "前线";
  return (
    <article className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={item.image} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-foreground-950">
          {catName}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-sm font-bold leading-snug text-foreground-950 md:text-base">{item.title}</h3>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{item.snippet}</p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {item.tags.map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800 whitespace-nowrap">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true"></i>
            {item.views}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true"></i>
            {item.time}
          </span>
        </div>
      </div>
    </article>
  );
}

function FeaturedCard({ item }: { item: FeedItem }) {
  const catName = categories.find((c) => c.id === item.category)?.name || "前线";
  return (
    <article className="group mt-6 flex flex-col overflow-hidden rounded-xl border border-primary-200 bg-background-50 lg:flex-row">
      <div className="relative aspect-[16/9] w-full overflow-hidden lg:aspect-auto lg:w-1/2">
        <img src={item.image} alt={item.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
          <i className="ri-flashlight-fill" aria-hidden="true"></i> 前线焦点
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-center p-5 md:p-7">
        <span className="text-xs font-semibold uppercase tracking-wide text-primary-600">{catName}</span>
        <h3 className="mt-2 text-lg font-extrabold leading-snug text-foreground-950 md:text-2xl">{item.title}</h3>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{item.snippet}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {item.tags.map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800 whitespace-nowrap">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-5 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true"></i> {item.views} 关注
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true"></i> {item.time}
          </span>
        </div>
      </div>
    </article>
  );
}